interface SheetData {
    headers: string[];
    items: Array<Record<string, any>>;
}

interface FilterConfig {
    primaryColumns: [string, string];
}

type GroupedItems = Array<{ dealerRow: Record<string, any>, items: Record<string, any>[] }>;

const parseNumber = (value: any): number => {
    const str = String(value ?? '').trim();
    if (['', '-'].includes(str)) return 0;
    return Math.max(0, parseFloat(str.replace(',', '.')));
};

const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

const hasValidData = (row: Record<string, any>, columns: string[]): boolean =>
    columns.some(col => parseNumber(row[col]) >= 0);

const handleFilterError = (
    showToast?: (message: string) => void,
    message: string = "El archivo de importación no contiene datos válidos en Items o Modelos"
): SheetData => {
    showToast?.(message);
    return { headers: [], items: [] };
};

const filterSheetRows = (
    items: Record<string, any>[],
    dealerColumn: string,
    monthColumns: string[]
): Record<string, any>[] => items.filter(row => {
    const mainValue = String(row[dealerColumn] ?? '').trim();
    return mainValue !== '' || hasValidData(row, monthColumns);
});

const determineRelevantColumns = (
    items: Record<string, any>[],
    primaryColumns: string[],
    possibleColumns: string[]
): string[] => {
    const relevantColumns = [...primaryColumns];

    possibleColumns.forEach(col => {
        if (items.some(row => parseNumber(row[col]) >= 1)) {
            relevantColumns.push(col);
        }
    });

    return relevantColumns;
};

const processSheetStructure = (
    sheet: SheetData,
    relevantColumns: string[]
): SheetData => ({
    headers: sheet.headers.filter(h => relevantColumns.includes(h)),
    items: sheet.items.map(row =>
        Object.fromEntries(relevantColumns.map(col => [col, row[col]]))
    )
});

const createDealerGroups = (
    items: Record<string, any>[],
    dealerColumn: string
): GroupedItems => {
    const groups: GroupedItems = [];
    let currentGroup: { dealerRow: Record<string, any>, items: Record<string, any>[] } | null = null;

    items.forEach(row => {
        const isDealerRow = String(row[dealerColumn] ?? '').trim() !== '';

        if (isDealerRow) {
            if (currentGroup) groups.push(currentGroup);
            currentGroup = { dealerRow: row, items: [] };
        } else {
            currentGroup?.items.push(row);
        }
    });

    if (currentGroup) groups.push(currentGroup);
    return groups;
};

const filterValidGroups = (
    groups: GroupedItems,
    dealerColumn: string,
    modelColumn: string,
    headers: string[],
    authorizedDealers: string[]
): GroupedItems => groups.filter(({ dealerRow, items }) => {
    const dealerName = String(dealerRow[dealerColumn]).trim().toUpperCase();
    const dataColumns = headers.filter(h => ![dealerColumn, modelColumn].includes(h));

    return authorizedDealers.includes(dealerName) &&
        items.some(item => hasValidData(item, dataColumns));
});

export const applyCustomFilters = (
    sheet: SheetData,
    primaryColumns: [string, string],
    dealers: string[],
    showToast?: (message: string) => void,
    errorMessage: string = "El archivo de importación no contiene datos válidos en Items o Modelos"
): SheetData => {
    let filteredSheet = applyAdvancedDealerFilters(
        deepClone(sheet),
        { primaryColumns },
        dealers.map(d => d.toUpperCase())
    );

    if (filteredSheet.items.length === 0) {
        return handleFilterError(showToast, errorMessage);
    }

    const processedItems = filteredSheet.items.map(item => {
        const processedItem: Record<string, any> = { ...item };
        filteredSheet.headers.forEach(header => {
            if (!primaryColumns.includes(header)) {
                processedItem[header] = parseNumber(processedItem[header]);
            }
        });
        return processedItem;
    });

    return {
        headers: filteredSheet.headers,
        items: processedItems
    };
};

const applyFiltersToSheet = (sheet: SheetData, config: FilterConfig): SheetData => {
    const clonedSheet = deepClone(sheet);
    const [dealerColumn, modelColumn] = config.primaryColumns;
    const monthColumns = clonedSheet.headers.filter(h => !config.primaryColumns.includes(h));

    const filteredItems = filterSheetRows(clonedSheet.items, dealerColumn, monthColumns);
    if (filteredItems.length === 0) return { headers: [], items: [] };

    const relevantColumns = determineRelevantColumns(filteredItems, config.primaryColumns, monthColumns);
    return processSheetStructure({ ...clonedSheet, items: filteredItems }, relevantColumns);
};

const applyAdvancedDealerFilters = (
    sheet: SheetData,
    config: FilterConfig,
    dealers: string[]
): SheetData => {
    const basicFiltered = applyFiltersToSheet(sheet, config);
    if (basicFiltered.items.length === 0) return basicFiltered;

    const [dealerColumn, modelColumn] = config.primaryColumns;
    const groups = createDealerGroups(basicFiltered.items, dealerColumn);
    const validGroups = filterValidGroups(
        groups,
        dealerColumn,
        modelColumn,
        basicFiltered.headers,
        dealers
    );

    return {
        headers: basicFiltered.headers,
        items: validGroups.flatMap(group => [group.dealerRow, ...group.items])
    };
};
